/**
 * The lesson builder (ADAPTIVE-ENGINE.md, "The lesson builder").
 *
 * Where the three adaptive systems meet. The difficulty engine says how hard
 * things should be, FSRS says what is due, gap detection says what this child
 * keeps missing — and this turns all of that into one ordered queue of
 * exercises that a nine-year-old will actually finish.
 *
 * Pure and deterministic, like everything else in `core`. Given the same request
 * it produces the same queue, seed included, which is what makes the weaving
 * rules property-testable instead of "looks about right".
 *
 * Two things it deliberately does **not** do:
 *
 * - **It never fetches.** Every pool is handed in already ordered by priority
 *   (due date, most recent mistake, weakest skill). The caller owns the
 *   database; this owns the teaching decisions.
 * - **It never silently discards.** Everything left out comes back in `dropped`
 *   with a reason, so "why did my child not get the speaking exercise" has an
 *   answer that does not require reading this file.
 */
import { resolveBand } from './difficulty.js';
import { SECONDS_BY_TYPE, isGentle, isSpeaking, isHighEffort, } from './exercises.js';
// --- caps -------------------------------------------------------------------
/** Cognitive load ceiling for this age (ADAPTIVE-ENGINE.md). */
export const MAX_NEW_LEXEMES = 6;
/** More than this and the lesson stops fitting its time budget. */
export const MAX_REVIEW_ITEMS = 8;
export const MAX_MISTAKE_ITEMS = 3;
export const MAX_WEAK_SKILL_ITEMS = 2;
/**
 * The hard ceiling, in seconds ([Product](../../../docs/PRODUCT.md), principle 5).
 *
 * A lesson may be shorter than its authored estimate. It may never be longer
 * than fifteen minutes, whatever the estimate says.
 */
export const HARD_CEILING_SECONDS = 15 * 60;
/**
 * How far apart the same item may repeat inside one lesson.
 *
 * Three exercises is roughly a minute — long enough that answering correctly the
 * second time is recall rather than short-term echo.
 */
export const MIN_REPEAT_GAP = 3;
/** Two wrong in a row is where the failure spiral starts. */
export const WRONG_STREAK_FOR_RELIEF = 2;
// --- determinism ------------------------------------------------------------
/** FNV-1a. Small, fast, and good enough to spread lesson seeds. */
const hashSeed = (seed) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
};
/**
 * mulberry32.
 *
 * The builder needs tie-breaks that vary between children and lessons but are
 * reproducible: a queue that cannot be rebuilt cannot be debugged, and
 * `Math.random` would make every test here a coin toss.
 */
export const seededRandom = (seed) => {
    let a = hashSeed(seed);
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};
// --- selection --------------------------------------------------------------
export const secondsFor = (exercise) => exercise.estSeconds === undefined
    ? SECONDS_BY_TYPE[exercise.type]
    : exercise.estSeconds;
/**
 * Why this exercise cannot be shown right now, or `null` if it can.
 *
 * A mic exercise whose only way out is `skip` is dropped rather than shown: a
 * child without a microphone should not be handed something they can only
 * dismiss.
 */
const admissible = (exercise, band, capabilities) => {
    if (exercise.minBand > band || exercise.maxBand < band)
        return 'band';
    if (exercise.requiresMic && !capabilities.mic) {
        if (exercise.nonMicFallback === undefined || exercise.nonMicFallback === 'skip') {
            return 'no-mic';
        }
    }
    if (exercise.requiresAudio &&
        !capabilities.audio &&
        exercise.noAudioFallback === undefined) {
        return 'no-audio';
    }
    return null;
};
const firstChapterOf = (taught, lexemeId) => {
    const ordinal = taught.firstChapterOrdinal.get(lexemeId);
    // Unknown means untaught: a lexeme no chapter introduces is not one to review.
    return ordinal === undefined ? Number.POSITIVE_INFINITY : ordinal;
};
/**
 * The invariant: never review what has not been taught.
 *
 * Applied to injected exercises only. Authored exercises *are* the lesson — if
 * one uses a word from a later chapter that is a content bug, and the linter
 * catches it before it ships; dropping it here would silently gut a lesson.
 */
const untaught = (exercise, taught) => exercise.lexemeIds.some((id) => firstChapterOf(taught, id) > taught.reachedChapterOrdinal);
/**
 * A lexeme this child is meeting for the first time in this lesson.
 *
 * "New" is the chapter they have just reached: everything earlier is revision,
 * and nothing later is admissible at all.
 */
const isNewLexeme = (taught, lexemeId) => firstChapterOf(taught, lexemeId) >= taught.reachedChapterOrdinal;
/**
 * Gather the pools into one admissible set, in priority order.
 *
 * Order matters twice over: an exercise that appears in two pools is kept as the
 * higher-priority one, and the caps are spent on the front of each pool, which
 * is why the caller sorts them.
 */
/**
 * The same exercises, with the speaking ones in front.
 *
 * A stable partition rather than a sort: within each half the authored order is
 * untouched, so an author's sequencing survives and only the two halves swap.
 */
const speakingFirst = (authored) => [
    ...authored.filter((one) => isSpeaking(one.type)),
    ...authored.filter((one) => !isSpeaking(one.type)),
];
function select(request, band, dropped) {
    const pools = [
        {
            source: 'authored',
            exercises: request.speakingFirst === true
                ? speakingFirst(request.authored)
                : request.authored,
            cap: Number.POSITIVE_INFINITY,
        },
        { source: 'mistake', exercises: request.mistakes, cap: MAX_MISTAKE_ITEMS },
        {
            source: 'review',
            exercises: request.review,
            // `Math.max(0, …)` rather than trusting the caller: a pet may only ever
            // add practice ([D-105](../../docs/DECISIONS.md#d-105)), and the clamp is
            // what makes that true of this line rather than of the callers.
            cap: MAX_REVIEW_ITEMS + Math.max(0, request.extraReview ?? 0),
        },
        { source: 'weak-skill', exercises: request.weakSkill, cap: MAX_WEAK_SKILL_ITEMS },
    ];
    const chosen = [];
    const seen = new Set();
    const newLexemes = new Set();
    for (const pool of pools) {
        let taken = 0;
        for (const exercise of pool.exercises) {
            const drop = (reason) => {
                dropped.push({ id: exercise.id, source: pool.source, reason });
            };
            if (seen.has(exercise.id)) {
                drop('duplicate');
                continue;
            }
            const inadmissible = admissible(exercise, band, request.capabilities);
            if (inadmissible !== null) {
                drop(inadmissible);
                continue;
            }
            if (pool.source !== 'authored' && untaught(exercise, request.taught)) {
                drop('untaught');
                continue;
            }
            if (taken >= pool.cap) {
                drop('pool-cap');
                continue;
            }
            /*
              A story is exempt, and it has to be.
      
              `isNewLexeme` is chapter-granular: it cannot tell a word introduced by
              *this* lesson from one introduced three lessons earlier in the same
              chapter. That is fine for an ordinary exercise, which touches one or two
              words — but a story is the chapter's recap and touches most of its
              vocabulary by design, so every one of them counts as "new" and the
              backstop drops the story entirely
              ([D-111](../../docs/DECISIONS.md#d-111)).
      
              Exempting it is safe because a story cannot introduce anything cold: the
              content linter refuses one unless at least seventy per cent of its words
              were taught by this chapter or earlier, which is a stronger guarantee
              than this cap was ever making.
      
              Found by a seed test that builds every authored lesson — the story came
              back with zero exercises, which on the path is a lesson that opens onto
              nothing.
            */
            if (pool.source === 'authored' && exercise.type !== 'story') {
                const introduced = exercise.lexemeIds.filter((id) => isNewLexeme(request.taught, id) && !newLexemes.has(id));
                // A backstop, not the real guard: the content linter is what stops a
                // lesson being authored with seven new words in the first place.
                if (newLexemes.size + introduced.length > MAX_NEW_LEXEMES) {
                    drop('new-item-cap');
                    continue;
                }
                for (const id of introduced)
                    newLexemes.add(id);
            }
            seen.add(exercise.id);
            taken += 1;
            chosen.push({
                exercise,
                source: pool.source,
                seconds: secondsFor(exercise),
            });
        }
    }
    return chosen;
}
/** What gets cut first when the lesson will not fit. */
const TRIM_ORDER = [
    'weak-skill',
    'review',
    'mistake',
    'authored',
];
/**
 * Trim to the time budget, before weaving rather than after.
 *
 * Cutting from a woven queue would re-open the rules it was built to satisfy —
 * remove the exercise between two dictations and there are now two dictations in
 * a row. So the set is settled first and ordered second.
 *
 * The last gentle exercise is never cut, because the queue still needs something
 * to open and close on.
 */
function trimToBudget(chosen, budget, dropped) {
    const kept = [...chosen];
    let total = kept.reduce((sum, c) => sum + c.seconds, 0);
    while (total > budget && kept.length > 1) {
        const index = victim(kept);
        const [gone] = kept.splice(index, 1);
        total -= gone.seconds;
        dropped.push({ id: gone.exercise.id, source: gone.source, reason: 'budget' });
    }
    return kept;
}
/** The least valuable exercise still in the set: lowest-priority source, latest. */
function victim(kept) {
    const lastGentle = kept.filter((c) => isGentle(c.exercise.type)).length === 1;
    const eligible = kept
        .map((candidate, index) => ({ candidate, index }))
        .filter(({ candidate }) => !(lastGentle && isGentle(candidate.exercise.type)));
    const rank = (e) => TRIM_ORDER.indexOf(e.candidate.source);
    // Never empty: `kept` holds at least two exercises and at most one of them is
    // the protected gentle one.
    const [worst] = [...eligible].sort((a, b) => rank(a) - rank(b) || b.index - a.index);
    return worst.index;
}
// --- weaving ----------------------------------------------------------------
const isInjected = (candidate) => candidate.source !== 'authored';
/**
 * An injected exercise landing on top of material that is already right there.
 *
 * Deliberately one-sided. Two authored exercises on the same word in a row are
 * *teaching* — hear it, then say it, then use it, which is what an intro lesson
 * is — and the builder has no business pulling them apart. What is an accident
 * is a review item, or a mistakes-pool repeat, arriving as an echo of the
 * exercise before it: answering correctly a moment later proves nothing, and it
 * reads to the child as the app losing its place.
 */
const echoes = (a, b) => (isInjected(a) || isInjected(b)) &&
    (a.exercise.id === b.exercise.id ||
        a.exercise.lexemeIds.some((id) => b.exercise.lexemeIds.includes(id)));
/**
 * Which rules placing `candidate` after `placed` would break.
 *
 * The whole weaving contract lives in this one function, so the builder and the
 * checker cannot drift apart: `weave` uses it to choose, `weaveViolations` uses
 * it to audit, and the tests assert against the same thing the builder obeyed.
 */
function breaks(candidate, placed) {
    const previous = placed[placed.length - 1];
    if (previous === undefined)
        return [];
    const broken = [];
    if (previous.exercise.type === candidate.exercise.type)
        broken.push('same-type');
    if (isHighEffort(previous.exercise.type) && isHighEffort(candidate.exercise.type)) {
        broken.push('back-to-back-effort');
    }
    if (isInjected(previous) && isInjected(candidate))
        broken.push('batched-review');
    if (placed.slice(-MIN_REPEAT_GAP).some((p) => echoes(p, candidate))) {
        broken.push('repeat-gap');
    }
    return broken;
}
/**
 * What each rule costs to break.
 *
 * Ordered by what it does to a tired child. Two speaking exercises back to back
 * is the one that ends a session; a word coming round one exercise early is
 * barely noticeable.
 */
const PENALTY = {
    'back-to-back-effort': 1000,
    'same-type': 100,
    'batched-review': 10,
    'repeat-gap': 1,
};
const penaltyOf = (broken) => broken.reduce((sum, rule) => sum + PENALTY[rule], 0);
/**
 * Every rule broken anywhere in a queue. The audit half of `breaks`.
 *
 * Every position is checked, the first included — it breaks nothing by
 * definition, and starting at zero means the audit makes no assumption about
 * where a queue is allowed to be wrong.
 */
export function weaveViolations(queue) {
    const found = [];
    for (let index = 0; index < queue.length; index++) {
        for (const rule of breaks(queue[index], queue.slice(0, index))) {
            found.push({ index, rule });
        }
    }
    return found;
}
/**
 * How good an exercise is at one end of a lesson.
 *
 * Both ends are recognition, never production: the first thirty seconds decide
 * whether the child stays, and the last one decides how they remember the
 * lesson. `preferInjected` picks the closer's flavour — something already
 * familiar is something they can definitely do.
 */
const endScore = (candidate, preferInjected) => (isGentle(candidate.exercise.type) ? 4 : 0) +
    (isHighEffort(candidate.exercise.type) ? 0 : 2) +
    (isInjected(candidate) === preferInjected ? 1 : 0);
/**
 * Take the highest-scoring candidate, breaking ties with the seeded stream.
 *
 * Reservoir sampling over the ties, so every tied candidate is equally likely
 * and the choice still depends only on the seed.
 */
function takeBest(pool, score, random) {
    let bestIndex = 0;
    let bestScore = score(pool[0]);
    let ties = 1;
    for (let i = 1; i < pool.length; i++) {
        const s = score(pool[i]);
        if (s > bestScore) {
            bestScore = s;
            bestIndex = i;
            ties = 1;
        }
        else if (s === bestScore) {
            ties += 1;
            if (random() < 1 / ties)
                bestIndex = i;
        }
    }
    return pool.splice(bestIndex, 1)[0];
}
/**
 * How much company a candidate still has, across all three adjacency rules.
 *
 * Each rule divides what is left into classes — this type against the others,
 * demanding against gentle, injected against authored — and the scarce class is
 * what separates the plentiful one. Spend the scarce class early and the last
 * four exercises are three speaking drills with nothing to put between them;
 * spend the plentiful class early and the separators are still there at the end.
 */
const plentiful = (candidate, pool) => pool.filter((c) => c.exercise.type === candidate.exercise.type).length +
    pool.filter((c) => isInjected(c) === isInjected(candidate)).length +
    pool.filter((c) => isHighEffort(c.exercise.type) === isHighEffort(candidate.exercise.type)).length;
/**
 * Choose the next exercise for the middle of the lesson.
 *
 * Penalty first, so a legal placement always beats an illegal one.
 *
 * Among legal placements it spends from whichever class it has most of, so the
 * separators survive to the end of the queue.
 */
function takeNext(pool, queue, closer, random) {
    const remaining = pool.length;
    const isLast = remaining === 1;
    const score = (candidate) => {
        const here = penaltyOf(breaks(candidate, queue));
        // The last one placed also has to sit next to the closer.
        const after = isLast ? penaltyOf(breaks(closer, [...queue, candidate])) : 0;
        // The scale keeps the preference a tie-break: it can never outweigh even the
        // cheapest broken rule.
        return plentiful(candidate, pool) - (here + after) * (3 * remaining + 1);
    };
    return takeBest(pool, score, random);
}
const queueCost = (queue) => weaveViolations(queue).reduce((sum, c) => sum + PENALTY[c.rule], 0);
/**
 * How much of the search tree the builder is willing to explore.
 *
 * Reached only by a pool with no legal order at all, where the bound never gets
 * tight enough to prune. Twenty thousand placements is a few milliseconds and
 * far more than any real lesson needs; past that the greedy answer stands.
 */
export const SEARCH_NODE_BUDGET = 20_000;
/**
 * Look for a better order than the greedy one found.
 *
 * The greedy pass is fast and usually right, but it commits: it will spend the
 * last gentle exercise four from the end and leave two speaking drills with
 * nothing to separate them. This is depth-first with the greedy result as the
 * opening bound, cheapest placement first, and it stops the moment it finds an
 * order that breaks nothing — which for a well-authored lesson is immediately.
 *
 * Bounded twice over, because a lesson build must never hang: costs only ever
 * grow, so any branch already at or above the best known total is abandoned, and
 * the whole search gives up after `SEARCH_NODE_BUDGET` placements.
 */
function searchForBetter(opener, middle, closer, greedy, random) {
    let best = greedy;
    let bestCost = queueCost(greedy);
    let nodes = 0;
    const visit = (placed, rest, cost) => {
        nodes += 1;
        if (rest.length === 0) {
            const total = cost + penaltyOf(breaks(closer, placed));
            if (total < bestCost) {
                bestCost = total;
                best = [...placed, closer];
            }
            return;
        }
        const children = rest
            .map((candidate) => ({
            candidate,
            cost: penaltyOf(breaks(candidate, placed)),
            company: plentiful(candidate, rest),
            tieBreak: random(),
        }))
            .sort((a, b) => a.cost - b.cost || b.company - a.company || a.tieBreak - b.tieBreak);
        for (const child of children) {
            if (bestCost === 0 || nodes >= SEARCH_NODE_BUDGET)
                return;
            const next = cost + child.cost;
            if (next >= bestCost)
                continue;
            placed.push(child.candidate);
            visit(placed, rest.filter((c) => c !== child.candidate), next);
            placed.pop();
        }
    };
    if (bestCost > 0)
        visit([opener], middle, 0);
    return best;
}
/** Order a settled set of exercises into a queue a child will finish. */
function weave(chosen, random) {
    const pool = [...chosen];
    if (pool.length === 0)
        return [];
    const opener = takeBest(pool, (c) => endScore(c, false), random);
    if (pool.length === 0)
        return [opener];
    const closer = takeBest(pool, (c) => endScore(c, true), random);
    const middle = [...pool];
    const greedy = [opener];
    while (pool.length > 0)
        greedy.push(takeNext(pool, greedy, closer, random));
    greedy.push(closer);
    return searchForBetter(opener, middle, closer, greedy, random);
}
// --- the builder ------------------------------------------------------------
/**
 * Build one lesson.
 *
 * Resolve the band, gather what is admissible, cut it to the time budget, then
 * order it. Everything left out is in `dropped`; every rule the pool made
 * unkeepable is in `compromises`.
 */
export function buildLesson(request) {
    const band = resolveBand(request.band, request.bandOverride);
    const dropped = [];
    const chosen = select(request, band, dropped);
    const budget = Math.min(request.estSeconds, HARD_CEILING_SECONDS);
    const kept = trimToBudget(chosen, budget, dropped);
    const queue = weave(kept, seededRandom(request.seed));
    return {
        band,
        queue,
        totalSeconds: queue.reduce((sum, c) => sum + c.seconds, 0),
        dropped,
        compromises: weaveViolations(queue),
    };
}
// --- during the lesson ------------------------------------------------------
/**
 * Whether the child has just got enough wrong in a row to need a break in the
 * pattern.
 *
 * A child who gets three wrong in a row is a child who is about to close the
 * app, so the relief goes in after the second. Skips do not count: a skip is
 * tiredness or disinterest, not failure, and treating it as failure would hand
 * an easy question to a child who is simply bored.
 */
export const needsRelief = (recent) => recent.length >= WRONG_STREAK_FOR_RELIEF &&
    recent.slice(-WRONG_STREAK_FOR_RELIEF).every((outcome) => outcome === 'incorrect');
/**
 * Bring the next gentle exercise forward to `from`.
 *
 * This is the whole of the "after two wrong in a row, insert an easy one" rule
 * as the client can implement it. There is no offline mode and no mid-lesson
 * fetch ([D-050](../../../docs/DECISIONS.md#d-050)): the lesson arrives whole, so
 * relief has to come from something already in the queue.
 *
 * Returns the queue unchanged when the next exercise is already gentle, or when
 * nothing gentle is left — a child near the end of a hard run is better served
 * by finishing than by being handed the closer early.
 */
export function pullGentleForward(queue, from) {
    const at = queue.findIndex((candidate, index) => index >= from && isGentle(candidate.exercise.type));
    if (at === -1 || at === from)
        return queue;
    const next = [...queue];
    const [gentle] = next.splice(at, 1);
    next.splice(from, 0, gentle);
    return next;
}
/**
 * Queue a wrongly-answered exercise to come round again, at least `minGap`
 * exercises later (the mistakes pool, ADAPTIVE-ENGINE.md §2).
 *
 * The gap is the point: answering correctly three exercises later is recall,
 * answering correctly immediately afterwards is short-term echo. The repeat
 * slides further along if it would land beside the same type of exercise.
 */
export function requeueForRepeat(queue, index, minGap = MIN_REPEAT_GAP) {
    const repeated = queue[index];
    if (repeated === undefined) {
        throw new RangeError(`no exercise at index ${String(index)}`);
    }
    const clashes = (candidate) => candidate !== undefined && candidate.exercise.type === repeated.exercise.type;
    let at = Math.min(index + 1 + minGap, queue.length);
    while (clashes(queue[at]) || clashes(queue[at - 1]))
        at += 1;
    const next = [...queue];
    next.splice(at, 0, repeated);
    return next;
}
//# sourceMappingURL=builder.js.map